import { forwardRef, HTMLAttributes, useCallback, useState } from 'react'
import * as Select from '@radix-ui/react-select'
import { find, map } from 'lodash'
import { ChevronDownIcon, HeartIcon } from 'lucide-react'
import Link from 'next/link'
import { useRouter } from 'next/router'

import Typography from '@/components/base/typography'
import { useAppStore } from '@/contexts/store'
import { useSSRGridLayout } from '@/hooks'
import { useLayoutStore } from '@/store/layout'

import { cn } from '@/lib/utils'
import { WPPageData } from '@/types'

export type FiltersProps = HTMLAttributes<HTMLDivElement> & {
  collection?: WPPageData['categories']
}

export const Filters = ({ collection, className }: FiltersProps) => {
  const router = useRouter()
  const { strings } = useAppStore()
  console.log(strings?.labels?.all_products)
  const [value, setValue] = useState<string>(find(collection, { link: router.asPath })?.name || '')

  const navigateTo = useCallback(
    (value: string) => {
      const current = find(collection, { name: value })
      current && router.push(current.link, undefined, { scroll: false })
    },
    [router, collection]
  )

  const handleValueChange = useCallback(
    (value: string) => {
      setValue(value)
      navigateTo(value)
    },
    [navigateTo]
  )

  return (
    <Select.Root value={value} onValueChange={handleValueChange}>
      <Select.Trigger
        aria-label={strings?.labels?.filters}
        className={cn(
          '-mr-[1rem] inline-flex min-w-[21.5rem] items-center justify-end space-x-[1rem] rounded-[0.5rem] px-[1rem] py-[1.1rem] outline-none',
          'focus-ring-dark',
          className
        )}
      >
        <span className="font-helvetica text-upper-small font-bold uppercase">
          <Select.Value placeholder={strings?.labels?.all_products} />
        </span>
        <Select.Icon>
          <ChevronDownIcon size={18} strokeWidth="3" />
        </Select.Icon>
      </Select.Trigger>

      <Select.Portal>
        <Select.Content position="popper" className="z-50 w-full rounded-[0.5rem] border bg-white">
          <Select.Viewport className="p-1">
            {map(collection, (filter, index) => (
              <Select.Item
                key={`filter-${index}`}
                value={filter.name}
                className={cn(
                  'font-helvetica text-upper-small font-bold uppercase',
                  'cursor-pointer rounded-[0.5rem] px-[2rem] py-[1.1rem]',
                  'data-[highlighted]:bg-black/0 data-[state=checked]:bg-black/5',
                  'focus-ring-dark'
                )}
              >
                <Select.ItemText>{filter.name}</Select.ItemText>
              </Select.Item>
            ))}
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  )
}

export type ToolbarProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    tools: WPPageData['acf']['layout']['header']['toolbar']
    filters: WPPageData['categories']
  }>

const Toolbar = forwardRef<HTMLDivElement, ToolbarProps>(
  ({ tools, filters = [], className, ...props }, ref) => {
    const { strings } = useAppStore()
    const layout = useSSRGridLayout()
    const setLayout = useLayoutStore((state) => state.setState)

    return (
      <div
        ref={ref}
        className={cn(
          'h-header-height w-full rounded-[0.7rem] px-[2rem] md:px-[3rem]',
          'border border-[#EBECEA] bg-white text-dark',
          'flex items-center justify-between',
          ['--radix-select-trigger-width:100%'],
          className
        )}
        {...props}
      >
        {tools?.back ? (
          <Link href={tools.back.link} className="flex items-center space-x-[1rem]" scroll={false}>
            <Typography variant="breadcrumbs" className="font-bold">
              ←
            </Typography>
            <Typography variant="breadcrumbs" className="font-bold">
              {strings?.labels?.back}
            </Typography>
          </Link>
        ) : null}

        {tools?.layout ? (
          <div className={cn('flex items-center space-x-[0.3rem]', 'hidden md:flex')}>
            {map(Array.from({ length: 3 }), (_, i) => (
              <button
                key={`layout-icon-${i}`}
                disabled={layout === i}
                onClick={() => setLayout({ gridLayout: i })}
                className={cn(
                  'group size-[3.5rem] rounded-[0.5rem] border border-dashed border-dark/30',
                  'flex items-center justify-center',
                  'disabled:border-solid',
                  'focus-ring-dark'
                )}
              >
                <span
                  className={cn('grid size-[1.9rem] opacity-30', 'group-disabled:opacity-100', {
                    'grid-cols-3 gap-[0.2rem]': i === 0,
                    'grid-cols-2 gap-[0.1rem]': i === 1,
                    'grid-cols-1': i === 2
                  })}
                >
                  {map(Array.from({ length: i === 0 ? 9 : i === 1 ? 4 : 1 }), (_, j) => (
                    <span
                      key={`shape-${j}`}
                      className={cn('col-span-1 bg-dark', {
                        'size-[0.5rem]': i === 0,
                        'size-[0.9rem]': i === 1,
                        'size-[1.9rem]': i === 2
                      })}
                    ></span>
                  ))}
                </span>
              </button>
            ))}
          </div>
        ) : null}

        {tools?.wishlist ? (
          <Link
            href="/wishlist"
            className="-m-[1rem] flex shrink-0 items-center justify-center space-x-[0.75rem] p-[1rem]"
            scroll={false}
          >
            <Typography variant="upper-small" className="font-bold uppercase">
              {strings?.wishlist?.my_wishlist}
            </Typography>
            <HeartIcon size={16} strokeWidth="1" className="fill-dark" />
          </Link>
        ) : null}

        {tools?.select ? (
          <Filters
            collection={filters}
            className={cn({
              'ml-auto': !(tools?.back || tools?.wishlist || tools?.layout)
            })}
          />
        ) : null}
      </div>
    )
  }
)

export default Toolbar
